import React from 'react';
import { ArrowLeft, Phone, Mail, MapPin, Instagram, Twitter, Clock, MessageCircle } from 'lucide-react';

interface ContactPageProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactPage: React.FC<ContactPageProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black overflow-y-auto animate-fade-in no-scrollbar">
      <div className="max-w-4xl mx-auto py-12 px-6">
        {/* Header */}
        <header className="flex justify-between items-center mb-12">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-neutral-500 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          <div className="text-xs font-mono text-neutral-600 uppercase tracking-widest">
            CONTACT_US // V1.0
          </div>
        </header>

        {/* Title */}
        <div className="mb-16">
          <h1 className="text-3xl md:text-5xl font-black tracking-tighter mb-3">
            Get in Touch <span className="text-amber-500">.</span>
          </h1>
          <div className="h-1 w-16 bg-amber-500 mb-4" />
          <p className="text-neutral-400 text-sm md:text-base max-w-xl leading-relaxed">
            Got a question, custom order request, or just want to say hi? We'd love to hear from you.
          </p>
        </div>

        {/* Contact Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">

          {/* Phone */}
          <a
            href="tel:+917896409312"
            className="group bg-neutral-900/50 rounded-2xl border border-white/5 p-8 hover:border-amber-500/30 transition-all duration-300 hover:bg-neutral-900/80"
          >
            <div className="w-12 h-12 bg-amber-600/10 rounded-xl border border-amber-500/20 flex items-center justify-center mb-5 group-hover:bg-amber-600/20 transition-colors">
              <Phone className="w-5 h-5 text-amber-400" />
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Phone</p>
            <p className="text-lg font-bold text-white group-hover:text-amber-400 transition-colors">+91 7896409312</p>
            <p className="text-xs text-neutral-500 mt-2">Tap to call directly</p>
          </a>

          {/* Email */}
          <a
            href="mailto:contact@yumecav.in"
            className="group bg-neutral-900/50 rounded-2xl border border-white/5 p-8 hover:border-amber-500/30 transition-all duration-300 hover:bg-neutral-900/80"
          >
            <div className="w-12 h-12 bg-amber-600/10 rounded-xl border border-amber-500/20 flex items-center justify-center mb-5 group-hover:bg-amber-600/20 transition-colors">
              <Mail className="w-5 h-5 text-amber-400" />
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Email</p>
            <p className="text-lg font-bold text-white group-hover:text-amber-400 transition-colors">contact@yumecav.in</p>
            <p className="text-xs text-neutral-500 mt-2">We typically respond within 24 hours</p>
          </a>

          {/* Location */}
          <div className="bg-neutral-900/50 rounded-2xl border border-white/5 p-8">
            <div className="w-12 h-12 bg-amber-600/10 rounded-xl border border-amber-500/20 flex items-center justify-center mb-5">
              <MapPin className="w-5 h-5 text-amber-400" />
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Location</p>
            <p className="text-lg font-bold text-white">Guwahati, Assam</p>
            <p className="text-xs text-neutral-500 mt-2">India</p>
          </div>

          {/* Business Hours */}
          <div className="bg-neutral-900/50 rounded-2xl border border-white/5 p-8">
            <div className="w-12 h-12 bg-amber-600/10 rounded-xl border border-amber-500/20 flex items-center justify-center mb-5">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Business Hours</p>
            <p className="text-lg font-bold text-white">Mon – Sat</p>
            <p className="text-xs text-neutral-500 mt-2">10:00 AM – 7:00 PM IST</p>
          </div>
        </div>

        {/* Social Section */}
        <div className="bg-neutral-900/50 rounded-2xl border border-white/5 p-8 mb-16">
          <div className="flex items-center gap-3 mb-6">
            <MessageCircle className="w-5 h-5 text-amber-400" />
            <h2 className="text-sm font-black uppercase tracking-widest text-neutral-400">Follow Us</h2>
          </div>
          <p className="text-neutral-400 text-sm mb-6 leading-relaxed">
            Stay updated with new drops, behind-the-scenes content, and community features.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.instagram.com/yumecav/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-5 py-3 bg-neutral-800/50 rounded-xl border border-white/5 hover:border-amber-500/30 hover:bg-amber-600/10 transition-all group"
            >
              <Instagram className="w-5 h-5 text-neutral-400 group-hover:text-amber-400 transition-colors" />
              <span className="text-sm font-bold text-neutral-300 group-hover:text-white transition-colors">Instagram</span>
            </a>
            <a
              href="#"
              className="flex items-center gap-3 px-5 py-3 bg-neutral-800/50 rounded-xl border border-white/5 hover:border-amber-500/30 hover:bg-amber-600/10 transition-all group"
            >
              <Twitter className="w-5 h-5 text-neutral-400 group-hover:text-amber-400 transition-colors" />
              <span className="text-sm font-bold text-neutral-300 group-hover:text-white transition-colors">Twitter</span>
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-8 border-t border-white/10 text-center">
          <p className="text-[10px] text-neutral-600 font-mono uppercase tracking-widest">
            &copy; {new Date().getFullYear()} YUMECAV. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
